import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import ThemedChartTooltip from '../../shared/ThemedChartTooltip';
import { getRiCongestionLeaderboard, getRiCongestionByCorridor } from '../../../api/auth';
import { RiLoading, RiEmpty, RiError, InsightCard, RiSparseNotice } from '../primitives/States';
import { orFailed, didFail, useReload } from '../lib/loadState';
import { congestionCorridorInsight } from '../lib/insights';
import { formatCorridorLabel, cleanLocationLabel } from '../lib/labelFormat';
import { RI_TABLE_FRAME, RI_THEAD, RI_TH, RI_ROW, RI_TD } from '../lib/tableStyles';

// 5B "Congestion by Corridor" bar chart — spec p.27: "color by severity".
// Adds a green / amber tier so low-congestion corridors read as healthy.
function severityColor(min) {
  if (min == null) return '#9CA3AF';
  if (min > 30) return '#B91C1C';
  if (min > 20) return '#EF4444';
  if (min > 10) return '#F59E0B';
  return '#10B981';
}

// 5A "Worst Congested Routes" Congestion column — spec p.27 calls for a
// "red gradient": darker = worse, no green/yellow.
function redGradient(min) {
  if (min == null) return '#9CA3AF';
  if (min > 45) return '#7F1D1D';
  if (min > 30) return '#B91C1C';
  if (min > 20) return '#DC2626';
  if (min > 10) return '#EF4444';
  return '#FCA5A5';
}

// Uniform 1-decimal formatter so rows showing e.g. "10" don't render alongside
// "9.5" / "10.2" — matches the precision convention used in CorridorsTab.
const fmt1 = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(1));
// Miles values get thousands separators to match the comma grouping used in
// the corridor / map-origins tables (markup #84). Keeps one decimal: "2,244.9".
const fmtMiles = (v) => (v == null || Number.isNaN(Number(v))
  ? '—'
  : Number(v).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

// Congestion tax % color per spec p.11: <5% green, 5-15% yellow, >15% red.
function taxColor(pct) {
  if (pct == null) return '';
  if (pct < 5)  return 'text-emerald-600 dark:text-emerald-400';
  if (pct <= 15) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

// Tracks the viewport width so the corridor chart's YAxis label area can scale
// with the screen. Updates on window resize; cleans up on unmount.
function useViewportWidth() {
  const [w, setW] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

export default function CongestionTab({ clientId, range }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const [leaderboard, setLeaderboard] = useState(null);
  const [byCorridor, setByCorridor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState({});
  const [reloadKey, retry] = useReload();
  const vw = useViewportWidth();
  // Three responsive label knobs:
  //   - YAxis width: how much horizontal space the label area takes.
  //   - tick font size: larger on big screens, smaller on phones.
  //   - max chars before truncating with "…".
  const yAxisWidth = vw < 640 ? 120 : vw < 1024 ? 200 : 300;
  const yAxisFont = vw < 640 ? 10 : 11;
  const maxLabelChars = vw < 640 ? 18 : vw < 1024 ? 30 : 46;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      orFailed(getRiCongestionLeaderboard(clientId, { from, to })),
      orFailed(getRiCongestionByCorridor(clientId, { from, to })),
    ]).then(([l, c]) => {
      if (cancelled) return;
      setFailed({ leaderboard: didFail(l), byCorridor: didFail(c) });
      setLeaderboard(didFail(l) ? [] : l?.data || []);
      setByCorridor(didFail(c) ? [] : c?.data || []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [clientId, from, to, reloadKey]);

  // Per markup-comment #47: the leaderboard ships multiple geocoded origins for
  // the same market (e.g. two Camarillo, CA rows with coords ~5 km apart and
  // near-identical metrics). Collapse them by their cleaned human-readable label
  // and average the numeric columns so the leaderboard reflects unique markets,
  // not geocoding noise. Coords for the representative row are preserved.
  const leaderboardDeduped = useMemo(() => {
    if (!leaderboard) return null;
    const groups = new Map();
    for (const r of leaderboard) {
      const label = cleanLocationLabel(r.origin_label);
      const key = label.toLowerCase();
      if (!groups.has(key)) {
        groups.set(key, {
          key, label,
          rows: [],
          routeCounts: {},
          driveSum: 0, driveN: 0,
          milesSum: 0, milesN: 0,
          congSum: 0, congN: 0,
          taxSum: 0, taxN: 0,
          jamSum: 0, jamN: 0,
        });
      }
      const g = groups.get(key);
      g.rows.push(r);
      if (r.route_summary) g.routeCounts[r.route_summary] = (g.routeCounts[r.route_summary] || 0) + 1;
      if (r.drive_time_hours != null) { g.driveSum += Number(r.drive_time_hours); g.driveN++; }
      if (r.drive_distance_miles != null) { g.milesSum += Number(r.drive_distance_miles); g.milesN++; }
      if (r.congestion_minutes != null) { g.congSum += Number(r.congestion_minutes); g.congN++; }
      if (r.congestion_tax_pct != null) { g.taxSum += Number(r.congestion_tax_pct); g.taxN++; }
      if (r.slow_jam_segments != null) { g.jamSum += Number(r.slow_jam_segments); g.jamN++; }
    }
    return [...groups.values()]
      .map((g) => {
        const rep = g.rows[0];
        const topRoute = Object.entries(g.routeCounts).sort((a, b) => b[1] - a[1])[0]?.[0]
          || rep.route_summary;
        return {
          origin_label: rep.origin_label,
          origin_lat: rep.origin_lat,
          origin_lon: rep.origin_lon,
          route_summary: topRoute,
          drive_time_hours: g.driveN ? g.driveSum / g.driveN : null,
          drive_distance_miles: g.milesN ? g.milesSum / g.milesN : null,
          congestion_minutes: g.congN ? g.congSum / g.congN : null,
          congestion_tax_pct: g.taxN ? g.taxSum / g.taxN : null,
          slow_jam_segments: g.jamN ? Math.round(g.jamSum / g.jamN) : null,
          merged_count: g.rows.length,
        };
      })
      .sort((a, b) => (b.congestion_minutes ?? 0) - (a.congestion_minutes ?? 0));
  }, [leaderboard]);

  const insight = useMemo(() => congestionCorridorInsight(byCorridor), [byCorridor]);
  const chartData = useMemo(() => (byCorridor || []).map((c) => ({
    corridor: c.corridor,
    corridor_label: formatCorridorLabel(c.corridor),
    avg: c.avg_congestion_min,
  })), [byCorridor]);

  if (loading) return <RiLoading />;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Worst Congested Routes</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Routes losing the most time to traffic. Red = severe congestion; nearby origins are merged into one row.</p>
        {failed.leaderboard ? (
          <RiError onRetry={retry} />
        ) : (!leaderboardDeduped || leaderboardDeduped.length === 0) ? (
          <RiEmpty message="No routes with meaningful congestion (>2 min) in this date range." />
        ) : (
          <div className="overflow-x-auto responsive-card-table-wrapper">
            <div className={RI_TABLE_FRAME}>
              <table className="w-full text-sm min-w-[800px] responsive-card-table">
              <thead className={RI_THEAD}>
                <tr>
                  <th className={`${RI_TH} text-left`}>Origin</th>
                  <th className={`${RI_TH} text-left`}>Route</th>
                  <th className={`${RI_TH} text-right`}>Drive hrs</th>
                  <th className={`${RI_TH} text-right`}>Miles</th>
                  <th className={`${RI_TH} text-right`}>Congestion</th>
                  <th className={`${RI_TH} text-right`}>Time lost %</th>
                  <th className={`${RI_TH} text-right`}>Slow/Jam zones</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-300">
                {leaderboardDeduped.map((r, i) => {
                  const lat = r.origin_lat != null ? Number(r.origin_lat).toFixed(2) : null;
                  const lon = r.origin_lon != null ? Number(r.origin_lon).toFixed(2) : null;
                  return (
                  <tr key={i} className={RI_ROW}>
                    <td className={`${RI_TD} text-gray-900 dark:text-white`} data-label="Origin">
                      <div>
                        {cleanLocationLabel(r.origin_label)}
                        {r.merged_count > 1 && (
                          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                            · {r.merged_count} origins (avg)
                          </span>
                        )}
                      </div>
                      {lat && lon && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          Lat: {lat} · Lon: {lon}
                        </div>
                      )}
                    </td>
                    <td className={`${RI_TD} text-gray-600 dark:text-gray-300`} data-label="Route">{r.route_summary ? formatCorridorLabel(r.route_summary) : '—'}</td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Drive hrs">{fmt1(r.drive_time_hours)}</td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Miles">{fmtMiles(r.drive_distance_miles)}</td>
                    <td className={`${RI_TD} text-right tabular-nums font-bold text-base`} style={{ color: redGradient(r.congestion_minutes) }} data-label="Congestion">
                      {r.congestion_minutes != null ? `${fmt1(r.congestion_minutes)} min` : '—'}
                    </td>
                    <td className={`${RI_TD} text-right tabular-nums font-medium ${taxColor(r.congestion_tax_pct)}`} data-label="Time lost %">
                      {r.congestion_tax_pct != null ? `${fmt1(r.congestion_tax_pct)}%` : '—'}
                    </td>
                    <td className={`${RI_TD} text-right tabular-nums ${r.slow_jam_segments > 20 ? 'text-red-600 dark:text-red-400 font-medium' : ''}`} data-label="Slow/Jam zones">
                      {r.slow_jam_segments}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Congestion by Corridor</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Average minutes lost on each highway corridor — color-coded by severity tier.</p>
        {failed.byCorridor ? (
          <RiError onRetry={retry} />
        ) : (!chartData || chartData.length === 0) ? (
          <RiEmpty message="No corridor congestion data in this date range." />
        ) : (
          <div className="w-full" style={{ height: Math.max(320, chartData.length * 36 + 60) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
                <XAxis type="number" tick={{ fontSize: 11 }} label={{ value: 'min', position: 'insideBottom', offset: -2, style: { fontSize: 11 } }} />
                <YAxis
                  type="category"
                  dataKey="corridor_label"
                  width={yAxisWidth}
                  interval={0}
                  tick={({ x, y, payload }) => {
                    const v = payload?.value ?? '';
                    const display = typeof v === 'string' && v.length > maxLabelChars
                      ? `${v.slice(0, maxLabelChars - 1).trimEnd()}…`
                      : v;
                    return (
                      <text x={x} y={y} dy={4} textAnchor="end" fontSize={yAxisFont} className="fill-gray-500 dark:fill-gray-400">
                        {display}
                      </text>
                    );
                  }}
                />
                <ThemedChartTooltip formatter={(v) => `${v} min`} />
                <Bar dataKey="avg" name="Avg congestion">
                  {chartData.map((d, i) => <Cell key={i} fill={severityColor(d.avg)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {chartData && chartData.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#10B981' }} />
              &lt;10 min
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#F59E0B' }} />
              10–20 min
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#EF4444' }} />
              20–30 min
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#B91C1C' }} />
              &gt;30 min
            </span>
          </div>
        )}
        {chartData && chartData.length > 0 && <RiSparseNotice count={chartData.length} />}
        {insight && (
          <div className="mt-3">
            <InsightCard title="Insight" tone="warn">
              <strong>{formatCorridorLabel(insight.worst.corridor)}</strong> averages <strong>{insight.worst.avg_congestion_min} min</strong> of congestion —
              {' '}<strong>{insight.delta} min</strong> more than <strong>{formatCorridorLabel(insight.best.corridor)}</strong> ({insight.best.avg_congestion_min} min).
            </InsightCard>
          </div>
        )}
      </div>
    </div>
  );
}
